/**
 * cdn-domain-cors-tab.tsx — CORS configuration tab for a CDN domain (act-as).
 *
 * Allowed origins, methods, headers, exposed headers, credentials toggle, max-age.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/cors
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Switch, Skeleton, Badge,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNCors,
  actAsCDNSecurityKeys,
  type CorsConfig,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

const DEFAULT_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS', 'PATCH', 'HEAD'];
const COMMON_HEADERS = ['Content-Type', 'Authorization', 'X-Requested-With', 'Accept', 'Origin'];

interface CorsForm {
  enabled: boolean;
  allow_origins: string[];
  allow_methods: string[];
  allow_headers: string[];
  expose_headers: string[];
  allow_credentials: boolean;
  max_age_secs: string;
  /* transient inputs */
  _origin_input: string;
  _header_input: string;
  _expose_input: string;
}

const DEFAULTS: CorsForm = {
  enabled: false,
  allow_origins: [],
  allow_methods: ['GET', 'POST', 'OPTIONS'],
  allow_headers: ['Content-Type', 'Authorization'],
  expose_headers: [],
  allow_credentials: false,
  max_age_secs: '86400',
  _origin_input: '',
  _header_input: '',
  _expose_input: '',
};

function hydrate(cfg: CorsConfig): CorsForm {
  return {
    enabled: cfg.enabled ?? false,
    allow_origins: cfg.allow_origins ?? [],
    allow_methods: cfg.allow_methods ?? ['GET', 'POST', 'OPTIONS'],
    allow_headers: cfg.allow_headers ?? ['Content-Type', 'Authorization'],
    expose_headers: cfg.expose_headers ?? [],
    allow_credentials: cfg.allow_credentials ?? false,
    max_age_secs: String(cfg.max_age_secs ?? 86400),
    _origin_input: '',
    _header_input: '',
    _expose_input: '',
  };
}

function toPayload(f: CorsForm): CorsConfig {
  return {
    enabled: f.enabled,
    allow_origins: f.allow_origins,
    allow_methods: f.allow_methods,
    allow_headers: f.allow_headers,
    expose_headers: f.expose_headers,
    allow_credentials: f.allow_credentials,
    max_age_secs: Number(f.max_age_secs) || 0,
  };
}

function payloadStr(f: CorsForm) { return JSON.stringify(toPayload(f)); }

interface Props { domainId: string }

export function CdnDomainCorsTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNCors(domainId);
  const [form, setForm] = useState<CorsForm>(DEFAULTS);
  const [snapshot, setSnapshot] = useState<CorsForm>(DEFAULTS);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data ? hydrate(q.data) : DEFAULTS;
    setForm(next); setSnapshot(next);
  }, [q.data]);

  const dirty = payloadStr(form) !== payloadStr(snapshot);
  const set = <K extends keyof CorsForm>(k: K, v: CorsForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const toggleMethod = (m: string) =>
    setForm((f) => ({
      ...f,
      allow_methods: f.allow_methods.includes(m)
        ? f.allow_methods.filter((x) => x !== m)
        : [...f.allow_methods, m],
    }));

  const addFromInput = (inputKey: '_origin_input' | '_header_input' | '_expose_input', listKey: 'allow_origins' | 'allow_headers' | 'expose_headers') => {
    const val = (form[inputKey] as string).trim();
    if (!val) return;
    const entries = val.split(',').map((s) => s.trim()).filter(Boolean);
    setForm((f) => ({
      ...f,
      [listKey]: [...f[listKey], ...entries.filter((e) => !f[listKey].includes(e))],
      [inputKey]: '',
    }));
  };

  const removeFromList = (listKey: 'allow_origins' | 'allow_headers' | 'expose_headers', idx: number) =>
    setForm((f) => ({ ...f, [listKey]: f[listKey].filter((_, i) => i !== idx) }));

  if (q.isLoading) return <Skeleton style={{ height: 300 }} />;
  if (q.isError) {
    return <TabError title={t('cdn_domain.cors_tab.load_error', 'Failed to load CORS configuration')} onRetry={() => q.refetch()} />;
  }

  return (
    <Section
      title={t('cdn_domain.cors_tab.title', 'CORS')}
      description={t('cdn_domain.cors_tab.description', 'Cross-Origin Resource Sharing policy for this domain.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* Enable toggle */}
        <Card>
          <CardBody>
            <SettingRow
              title={t('cdn_domain.cors_tab.field_enabled', 'CORS enabled')}
              description={t('cdn_domain.cors_tab.field_enabled_desc', 'Add CORS headers to responses from this domain.')}
              control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />}
            />
          </CardBody>
        </Card>

        {form.enabled && (
          <>
            {/* Origins */}
            <Card>
              <CardHeader>
                <CardTitle>{t('cdn_domain.cors_tab.section_origins', 'Allowed Origins')}</CardTitle>
                <CardDescription>{t('cdn_domain.cors_tab.section_origins_desc', 'Use * to allow all origins.')}</CardDescription>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
                  {form.allow_origins.map((o, i) => (
                    <Badge key={i} tone="neutral" appearance="subtle">
                      {o}
                      <IconButton variant="ghost" size="sm" aria-label="Remove" style={{ marginLeft: 2 }} onClick={() => removeFromList('allow_origins', i)}>
                        <X size={11} />
                      </IconButton>
                    </Badge>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                  <Input
                    value={form._origin_input}
                    placeholder="https://example.com"
                    style={{ flex: 1 }}
                    onChange={(e) => set('_origin_input', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFromInput('_origin_input', 'allow_origins'); } }}
                  />
                  <Button variant="secondary" size="sm" startIcon={<Plus size={13} />} onClick={() => addFromInput('_origin_input', 'allow_origins')}>
                    {t('cdn_domain.cors_tab.add_origin', 'Add')}
                  </Button>
                </div>
              </CardBody>
            </Card>

            {/* Methods */}
            <Card>
              <CardHeader>
                <CardTitle>{t('cdn_domain.cors_tab.section_methods', 'Allowed Methods')}</CardTitle>
              </CardHeader>
              <CardBody>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                  {DEFAULT_METHODS.map((m) => (
                    <Badge
                      key={m}
                      tone={form.allow_methods.includes(m) ? 'info' : 'neutral'}
                      appearance={form.allow_methods.includes(m) ? 'solid' : 'subtle'}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleMethod(m)}
                    >
                      {m}
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* Allow Headers */}
            <Card>
              <CardHeader>
                <CardTitle>{t('cdn_domain.cors_tab.section_allow_headers', 'Allowed Headers')}</CardTitle>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
                  {form.allow_headers.map((h, i) => (
                    <Badge key={i} tone="neutral" appearance="subtle">
                      {h}
                      <IconButton variant="ghost" size="sm" aria-label="Remove" style={{ marginLeft: 2 }} onClick={() => removeFromList('allow_headers', i)}>
                        <X size={11} />
                      </IconButton>
                    </Badge>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
                  {COMMON_HEADERS.filter((h) => !form.allow_headers.includes(h)).map((h) => (
                    <Badge key={h} tone="neutral" appearance="outline" size="sm" style={{ cursor: 'pointer' }}
                      onClick={() => setForm((f) => ({ ...f, allow_headers: [...f.allow_headers, h] }))}>
                      + {h}
                    </Badge>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                  <Input value={form._header_input} placeholder="X-Custom-Header" style={{ flex: 1 }}
                    onChange={(e) => set('_header_input', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFromInput('_header_input', 'allow_headers'); } }} />
                  <Button variant="secondary" size="sm" startIcon={<Plus size={13} />} onClick={() => addFromInput('_header_input', 'allow_headers')}>
                    {t('cdn_domain.cors_tab.add_header', 'Add')}
                  </Button>
                </div>
              </CardBody>
            </Card>

            {/* Expose Headers */}
            <Card>
              <CardHeader>
                <CardTitle>{t('cdn_domain.cors_tab.section_expose_headers', 'Exposed Headers')}</CardTitle>
                <CardDescription>{t('cdn_domain.cors_tab.section_expose_desc', 'Headers exposed to browser JavaScript.')}</CardDescription>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
                  {form.expose_headers.map((h, i) => (
                    <Badge key={i} tone="neutral" appearance="subtle">
                      {h}
                      <IconButton variant="ghost" size="sm" aria-label="Remove" style={{ marginLeft: 2 }} onClick={() => removeFromList('expose_headers', i)}>
                        <X size={11} />
                      </IconButton>
                    </Badge>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                  <Input value={form._expose_input} placeholder="X-Response-Header" style={{ flex: 1 }}
                    onChange={(e) => set('_expose_input', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFromInput('_expose_input', 'expose_headers'); } }} />
                  <Button variant="secondary" size="sm" startIcon={<Plus size={13} />} onClick={() => addFromInput('_expose_input', 'expose_headers')}>
                    {t('cdn_domain.cors_tab.add_expose', 'Add')}
                  </Button>
                </div>
              </CardBody>
            </Card>

            {/* Credentials + max-age */}
            <Card>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                <SettingRow
                  title={t('cdn_domain.cors_tab.field_credentials', 'Allow credentials')}
                  description={t('cdn_domain.cors_tab.field_credentials_desc', 'Set Access-Control-Allow-Credentials: true. Requires explicit origin (not *).')}
                  control={<Switch checked={form.allow_credentials} onCheckedChange={(v) => set('allow_credentials', v)} />}
                />
                <div style={{ marginTop: 'var(--vn-space-3)', maxWidth: 200 }}>
                  <Field label={t('cdn_domain.cors_tab.field_max_age', 'Max age (seconds)')}
                    description={t('cdn_domain.cors_tab.field_max_age_desc', 'How long preflight results are cached.')}>
                    {(p) => (
                      <NumericInput {...p} value={form.max_age_secs}
                        onChange={(e) => set('max_age_secs', e.target.value)} />
                    )}
                  </Field>
                </div>
              </CardBody>
            </Card>
          </>
        )}

        <ActionBar
          end={
            <>
              <Button variant="secondary" disabled={!dirty} onClick={() => setForm(snapshot)}>{t('common.cancel', 'Cancel')}</Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>{t('cdn_domain.cors_tab.action_save', 'Save')}</Button>
            </>
          }
        />
      </div>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.cors_tab.confirm_save_title', 'Save CORS configuration')}
        summary={t('cdn_domain.cors_tab.save_summary', 'Update CORS policy — origins, methods, headers, and credentials.')}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.cors(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/cors`, toPayload(form))}
        onSuccess={() => setSnapshot(form)}
      />
    </Section>
  );
}
